import React from 'react';
import Icon from '../../../components/AppIcon';

import marketplaceService from '../../../services/marketplaceService';

const statusConfig = {
  scheduled: { color: 'bg-blue-500/10 text-blue-600 border-blue-500/20', label: 'Scheduled', icon: 'Clock' },
  active: { color: 'bg-green-500/10 text-green-600 border-green-500/20', label: 'Active', icon: 'Zap' },
  ended: { color: 'bg-gray-500/10 text-gray-500 border-gray-500/20', label: 'Ended', icon: 'MinusCircle' },
  sold: { color: 'bg-purple-500/10 text-purple-600 border-purple-500/20', label: 'Sold', icon: 'CheckCircle' },
  cancelled: { color: 'bg-red-500/10 text-red-500 border-red-500/20', label: 'Cancelled', icon: 'XCircle' },
};

const StatusBadge = ({ status }) => {
  const cfg = statusConfig?.[status] || statusConfig?.ended;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg?.color}`}>
      <Icon name={cfg?.icon} size={11} />
      {cfg?.label}
    </span>
  );
};

const AuctionTable = ({ auctions, loading, onRefresh }) => {
  const handleCancel = async (auctionId) => {
    if (!confirm('Cancel this auction?')) return;
    const { error } = await marketplaceService?.cancelAuction(auctionId);
    if (!error) {
      onRefresh?.();
    } else {
      alert('Failed to cancel: ' + error);
    }
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-xl overflow-hidden animate-pulse">
        <div className="h-12 bg-muted/40 border-b border-border" />
        {[1, 2, 3, 4]?.map(i => (
          <div key={i} className="h-16 border-b border-border/50 bg-muted/10" />
        ))}
      </div>
    );
  }

  if (auctions?.length === 0) {
    return (
      <div className="bg-card border border-border rounded-xl p-16 text-center">
        <Icon name="Gavel" size={48} className="mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-semibold text-foreground mb-2">No auctions found</h3>
        <p className="text-muted-foreground text-sm">No auctions match the selected filter</p>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Desktop Table Header */}
      <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-3 bg-muted/30 border-b border-border text-xs font-semibold text-muted-foreground uppercase tracking-wide">
        <div className="col-span-4">Item</div>
        <div className="col-span-2">Status</div>
        <div className="col-span-1 text-right">Bids</div>
        <div className="col-span-2 text-right">Current / Final</div>
        <div className="col-span-2 text-right">End Date</div>
        <div className="col-span-1 text-right">Actions</div>
      </div>
      {/* Table Rows */}
      <div className="divide-y divide-border/50">
        {auctions?.map(auction => {
          const isSold = auction?.status === 'sold';
          const finalPrice = isSold
            ? parseFloat(auction?.winning_bid || auction?.current_bid || 0)
            : parseFloat(auction?.current_bid || auction?.starting_bid || 0);
          const bidCount = auction?.bid_count || auction?.bids?.length || 0;
          const endDate = auction?.end_date ? new Date(auction.end_date) : null;
          const isExpired = endDate && endDate < new Date();

          return (
            <div key={auction?.id} className="hover:bg-muted/20 transition-colors">
              {/* Desktop Row */}
              <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 items-center">
                {/* Item */}
                <div className="col-span-4 flex items-center gap-3 min-w-0">
                  <div className="w-12 h-12 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                    {auction?.featured_image_url || auction?.collectible?.primary_image_url ? (
                      <img
                        src={auction?.featured_image_url || auction?.collectible?.primary_image_url}
                        alt={auction?.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="Image" size={20} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{auction?.title}</p>
                    <p className="text-xs text-muted-foreground truncate">
                      {auction?.collectible?.collectible_type || auction?.category?.name || 'Collectible'}
                    </p>
                  </div>
                </div>
                {/* Status */}
                <div className="col-span-2">
                  <StatusBadge status={auction?.status} />
                </div>
                {/* Bids */}
                <div className="col-span-1 text-right">
                  <span className="text-sm font-semibold text-foreground">{bidCount}</span>
                </div>
                {/* Price */}
                <div className="col-span-2 text-right">
                  <p className={`text-sm font-bold ${isSold ? 'text-green-500' : 'text-primary'}`}>
                    ${finalPrice?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                  {isSold && <p className="text-xs text-green-500/70">Final Sale</p>}
                  {!isSold && auction?.reserve_price && (
                    <p className="text-xs text-muted-foreground">
                      Reserve: ${parseFloat(auction?.reserve_price)?.toFixed(2)}
                    </p>
                  )}
                </div>
                {/* End Date */}
                <div className="col-span-2 text-right">
                  {endDate ? (
                    <>
                      <p className="text-sm text-foreground">{endDate?.toLocaleDateString()}</p>
                      <p className={`text-xs ${isExpired ? 'text-muted-foreground' : 'text-warning'}`}>
                        {isExpired ? 'Ended' : `${Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24))}d left`}
                      </p>
                    </>
                  ) : (
                    <span className="text-xs text-muted-foreground">—</span>
                  )}
                </div>
                {/* Actions */}
                <div className="col-span-1 flex justify-end">
                  {auction?.status === 'scheduled' && (
                    <button
                      onClick={() => handleCancel(auction?.id)}
                      className="p-2 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors min-w-[36px] min-h-[36px] flex items-center justify-center"
                      title="Cancel Auction"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  )}
                  {(auction?.status === 'active' || auction?.status === 'sold' || auction?.status === 'ended') && (
                    <div className="w-8 h-8 flex items-center justify-center">
                      <Icon name="Eye" size={14} className="text-muted-foreground" />
                    </div>
                  )}
                </div>
              </div>

              {/* Mobile Card */}
              <div className="md:hidden px-4 py-4">
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                    {auction?.featured_image_url || auction?.collectible?.primary_image_url ? (
                      <img
                        src={auction?.featured_image_url || auction?.collectible?.primary_image_url}
                        alt={auction?.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Icon name="Image" size={20} className="text-muted-foreground" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <p className="text-sm font-medium text-foreground truncate">{auction?.title}</p>
                      <StatusBadge status={auction?.status} />
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">
                      {auction?.collectible?.collectible_type || auction?.category?.name || 'Collectible'}
                    </p>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div>
                        <p className="text-xs text-muted-foreground">Price</p>
                        <p className={`text-sm font-bold ${isSold ? 'text-green-500' : 'text-primary'}`}>
                          ${finalPrice?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Bids</p>
                        <p className="text-sm font-semibold text-foreground">{bidCount}</p>
                      </div>
                      {endDate && (
                        <div>
                          <p className="text-xs text-muted-foreground">End Date</p>
                          <p className={`text-xs font-medium ${isExpired ? 'text-muted-foreground' : 'text-warning'}`}>
                            {isExpired ? 'Ended' : `${Math.ceil((endDate - new Date()) / (1000 * 60 * 60 * 24))}d left`}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                  {auction?.status === 'scheduled' && (
                    <button
                      onClick={() => handleCancel(auction?.id)}
                      className="p-2.5 rounded-lg text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors flex-shrink-0 min-w-[44px] min-h-[44px] flex items-center justify-center"
                      title="Cancel Auction"
                    >
                      <Icon name="X" size={16} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {/* Footer summary */}
      <div className="px-4 sm:px-6 py-3 bg-muted/20 border-t border-border flex items-center justify-between flex-wrap gap-2">
        <p className="text-xs text-muted-foreground">{auctions?.length} auction{auctions?.length !== 1 ? 's' : ''}</p>
        <p className="text-xs text-muted-foreground">
          Total value:{' '}
          <span className="font-semibold text-foreground">
            ${auctions?.filter(a => a?.status === 'sold')?.reduce((s, a) => s + parseFloat(a?.winning_bid || a?.current_bid || 0), 0)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </p>
      </div>
    </div>
  );
};

export default AuctionTable;
